import { Badge, Card, Clickable } from '@warp-ds/react';
import { memo, useMemo } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { FavoriteButton } from '@/components/favoriteButton/FavoriteButton';
import { AD_TYPE_PRIVATE, COUNTRY_NORWAY } from '@/constants';
import type { Filters, ResultItem as ResultItemType } from '@/domain';
import { trackResultItem } from '@/lib/tracking/tracking';
import type { LoginId } from '@/types/common';
import { resultItemLocationString, resultItemPriceString } from '@/utils/formatting';
import { buildAdUrl } from '@/utils/urlPrepender';
import { ResultItemImage } from './components/ResultItemImage';
import styles from './ResultItem.module.css';

export type ResultItemProps = {
    ad: ResultItemType;
    rank: number;
    loginId?: LoginId;
    filters: Filters;
    loading?: 'eager' | 'lazy';
};

export const ResultItem = memo(({ filters, ad, rank, loading, loginId }: ResultItemProps) => {
    const { contextPath } = useConfig();

    const isTransactionalAd = ad.ad_type === AD_TYPE_PRIVATE && ad.country === COUNTRY_NORWAY;

    const adUrl = useMemo(
        () =>
            buildAdUrl({
                contextPath,
                adId: ad.id,
                bookingFrom: filters.booking_from,
                bookingTo: filters.booking_to,
                numberOfGuests: filters.no_of_beds_from,
            }),
        [contextPath, ad.id, filters.booking_from, filters.booking_to, filters.no_of_beds_from],
    );

    const clickAd = () => trackResultItem(ad.id, ad.heading, rank + 1, ad.upsell === true, ad.fus_classified_product_id);

    return (
        <div className="relative min-w-0">
            <Card className={styles.resultItem} data-testid={`resultItem-${ad.id}`}>
                <article className="relative">
                    <div>
                        {isTransactionalAd && (
                            <Badge variant="info" position="top-left" className="px-8 gap-4">
                                Enkel bestilling
                            </Badge>
                        )}
                        <ResultItemImage imageUrl={ad.imageurl} loading={loading} />
                    </div>
                    <div className="p-16">
                        {ad.upsell && (
                            <Badge variant="sponsored" className="p-4 mb-12">
                                Betalt plassering
                            </Badge>
                        )}
                        <p className="text-12 text-gray-500 truncate pr-32">{resultItemLocationString(ad)}</p>
                        <h3 className="truncate text-16 font-normal text-gray-800">
                            <Clickable
                                href={adUrl}
                                target="_blank"
                                title={ad.heading}
                                className="text-current hover:no-underline focus:no-underline"
                                onClick={clickAd}
                                rel="noopener noreferrer">
                                {ad.heading}
                            </Clickable>
                        </h3>
                        {resultItemPriceString(ad)}
                        <p className="text-14 text-gray-500 mb-0" data-testid="bedroomCapacity">
                            {ad.no_of_bedrooms} soverom <span>•</span> {ad.sleeping_capacity} sengeplasser
                        </p>
                        <p className="text-14 text-gray-500 mt-8 mb-0">{ad.ad_type !== AD_TYPE_PRIVATE ? 'Bedrift' : 'Privat'}</p>
                    </div>
                </article>
            </Card>
            <FavoriteButton
                itemId={ad.id}
                variant="heart-on-image"
                itemType="Ad"
                loginId={loginId}
                wrapperClassName="absolute z-10 top-0 right-0 m-4"
            />
        </div>
    );
});

ResultItem.displayName = 'ResultItem';
